import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Search, Trash2, Mail, Phone, ChevronDown, RefreshCw, Filter, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const STATUS = [
  { v: "new", label: "New", color: "bg-copper text-ink-900" },
  { v: "contacted", label: "Contacted", color: "bg-amber-400 text-ink-900" },
  { v: "quoted", label: "Quoted", color: "bg-sky-400 text-ink-900" },
  { v: "closed", label: "Closed", color: "bg-steel-500 text-ink-900" },
];
const STATUS_MAP = Object.fromEntries(STATUS.map((s) => [s.v, s]));

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.new;
  return (
    <span className={`inline-block px-2 py-1 font-mono-tech text-[9px] font-bold ${s.color}`}>
      {s.label.toUpperCase()}
    </span>
  );
}

function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, pw);
      toast.success("Welcome back.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="admin-login"
      className="min-h-[80vh] flex items-center justify-center px-5 py-16"
    >
      <div className="w-full max-w-md border border-ink-600 bg-ink-800 p-8 md:p-10 shadow-[8px_8px_0_#050E1B]">
        <div className="flex items-center gap-2 font-mono-tech text-[10px] text-copper mb-6">
          <ShieldCheck size={14} /> ADMIN AREA
        </div>
        <h1 className="font-display text-4xl leading-[0.95] mb-2 chrome-text">Sign in</h1>
        <p className="text-steel-400 text-sm mb-8">WRE staff only.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">EMAIL</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-ink-900 border border-ink-600 px-3 py-3 outline-none focus:border-copper text-sm"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">PASSWORD</label>
            <input
              type="password"
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-ink-900 border border-ink-600 px-3 py-3 outline-none focus:border-copper text-sm"
              autoComplete="current-password"
            />
          </div>
          {err && (
            <div data-testid="admin-login-error" className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-copper w-full !py-4 disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}

function QuoteRow({ q, onUpdate, onDelete, expanded, onToggle }) {
  const [notes, setNotes] = useState(q.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const setStatus = async (v) => {
    setSaving(true);
    await onUpdate(q.id, { status: v, admin_notes: notes });
    setSaving(false);
  };
  const saveNotes = async () => {
    setSaving(true);
    await onUpdate(q.id, { status: q.status, admin_notes: notes });
    setSaving(false);
  };

  return (
    <>
      <tr
        data-testid={`admin-quote-row-${q.id}`}
        className={`border-b border-ink-600 hover:bg-ink-800 cursor-pointer ${expanded ? "bg-ink-800" : ""}`}
        onClick={() => onToggle(q.id)}
      >
        <td className="px-4 py-4 font-mono-tech text-[10px] text-steel-400 whitespace-nowrap">
          {new Date(q.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </td>
        <td className="px-4 py-4">
          <div className="font-display text-lg leading-tight">{q.full_name}</div>
          <div className="font-mono-tech text-[9px] text-steel-500">{q.email}</div>
        </td>
        <td className="px-4 py-4 text-sm">
          {q.vehicle_make} {q.vehicle_model}
          <div className="font-mono-tech text-[9px] text-steel-500">
            {q.vehicle_year} · {q.part_type}
          </div>
        </td>
        <td className="px-4 py-4"><StatusPill status={q.status} /></td>
        <td className="px-4 py-4 text-right">
          <ChevronDown
            size={16}
            className={`inline-block transition-transform ${expanded ? "rotate-180 text-copper" : "text-steel-400"}`}
          />
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`admin-quote-expanded-${q.id}`}>
          <td colSpan={5} className="bg-ink-800 border-b border-copper/40 px-4 py-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-6 text-sm space-y-3">
                <div className="font-mono-tech text-[10px] text-copper">CUSTOMER MESSAGE</div>
                <p className="whitespace-pre-wrap text-steel-200 leading-relaxed">{q.message}</p>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-ink-600 text-[13px]">
                  <div><span className="text-steel-500 font-mono-tech text-[9px] block">PHONE</span>{q.phone}</div>
                  <div><span className="text-steel-500 font-mono-tech text-[9px] block">COMPANY</span>{q.company || "—"}</div>
                  <div><span className="text-steel-500 font-mono-tech text-[9px] block">VRN</span>{q.vrn || "—"}</div>
                  <div><span className="text-steel-500 font-mono-tech text-[9px] block">SKU</span>{q.product_sku || "—"}</div>
                </div>
                <div className="flex flex-wrap gap-2 pt-4">
                  <a href={`mailto:${q.email}`} className="btn-ghost !px-3 !py-2 !text-[10px]">
                    <Mail size={12} /> EMAIL
                  </a>
                  <a href={`tel:${q.phone.replace(/\s+/g, "")}`} className="btn-ghost !px-3 !py-2 !text-[10px]">
                    <Phone size={12} /> CALL
                  </a>
                  <button
                    onClick={() => {
                      if (window.confirm("Delete this quote? This cannot be undone.")) onDelete(q.id);
                    }}
                    data-testid={`admin-quote-delete-${q.id}`}
                    className="btn-ghost !px-3 !py-2 !text-[10px] !text-red-400 !border-red-500/30 hover:!border-red-500"
                  >
                    <Trash2 size={12} /> DELETE
                  </button>
                </div>
              </div>

              <div className="md:col-span-6 space-y-4">
                <div>
                  <div className="font-mono-tech text-[10px] text-copper mb-2">STATUS</div>
                  <div className="flex flex-wrap gap-2">
                    {STATUS.map((s) => (
                      <button
                        key={s.v}
                        onClick={() => setStatus(s.v)}
                        disabled={saving}
                        data-testid={`admin-quote-status-${s.v}-${q.id}`}
                        className={`px-3 py-2 font-mono-tech text-[10px] border transition-colors ${
                          q.status === s.v
                            ? "border-copper bg-copper text-ink-900"
                            : "border-ink-600 text-steel-300 hover:border-copper hover:text-copper"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] text-copper mb-2">INTERNAL NOTES</div>
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid={`admin-quote-notes-${q.id}`}
                    className="w-full bg-ink-900 border border-ink-600 px-3 py-3 outline-none focus:border-copper text-sm resize-none"
                    placeholder="Only visible to WRE staff."
                  />
                  <button
                    onClick={saveNotes}
                    disabled={saving}
                    data-testid={`admin-quote-save-notes-${q.id}`}
                    className="btn-copper mt-2 !py-2 disabled:opacity-60"
                  >
                    {saving ? "Saving…" : "Save Notes"}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Dashboard() {
  const { user, logout, authAxios } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const ax = authAxios();
      const [qs, st] = await Promise.all([
        ax.get(`/admin/quotes${statusFilter ? `?status=${statusFilter}` : ""}`),
        ax.get(`/admin/quotes/stats`),
      ]);
      setQuotes(qs.data);
      setStats(st.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        nav("/admin");
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => { cancelled = true; };
     
  }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!q) return quotes;
    const s = q.toLowerCase();
    return quotes.filter(
      (x) =>
        x.full_name.toLowerCase().includes(s) ||
        x.email.toLowerCase().includes(s) ||
        x.vehicle_make.toLowerCase().includes(s) ||
        x.vehicle_model.toLowerCase().includes(s) ||
        (x.vrn || "").toLowerCase().includes(s) ||
        (x.company || "").toLowerCase().includes(s),
    );
  }, [q, quotes]);

  const update = async (id, patch) => {
    try {
      const ax = authAxios();
      const { data } = await ax.patch(`/admin/quotes/${id}`, patch);
      setQuotes((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Updated");
      const st = await ax.get(`/admin/quotes/stats`);
      setStats(st.data);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    try {
      const ax = authAxios();
      await ax.delete(`/admin/quotes/${id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== id));
      setExpanded(null);
      toast.success("Deleted");
      const st = await ax.get(`/admin/quotes/stats`);
      setStats(st.data);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-[80vh]">
      {/* Bar */}
      <section className="border-b border-ink-600 bg-ink-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 font-mono-tech text-[10px] text-copper">
            <ShieldCheck size={14} /> ADMIN · {user?.email}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={load}
              className="btn-ghost !px-3 !py-2"
              data-testid="admin-refresh"
              aria-label="Refresh"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={() => { logout(); nav("/admin"); }}
              className="btn-ghost !px-3 !py-2"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-ink-600">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-5 border-t border-ink-600">
          {[
            ["Total", stats.total],
            ["New", stats.by_status?.new || 0],
            ["Contacted", stats.by_status?.contacted || 0],
            ["Quoted", stats.by_status?.quoted || 0],
            ["Closed", stats.by_status?.closed || 0],
          ].map(([k, v], i) => (
            <div
              key={k}
              className={`p-6 md:p-8 border-ink-600 ${i < 4 ? "md:border-r" : ""} ${i < 3 ? "border-b md:border-b-0" : ""}`}
            >
              <div className="font-mono-tech text-[9px] text-steel-400 mb-2">{k.toUpperCase()}</div>
              <div className="font-display text-4xl chrome-text">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-ink-600 bg-ink-900 sticky top-[73px] z-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-4 flex flex-wrap gap-3 items-center">
          <span className="font-mono-tech text-[10px] text-steel-400 flex items-center gap-2">
            <Filter size={12} /> FILTER
          </span>
          <button
            data-testid="admin-filter-all"
            onClick={() => setStatusFilter("")}
            className={`px-3 py-2 font-mono-tech text-[10px] border ${
              !statusFilter ? "border-copper bg-copper text-ink-900" : "border-ink-600 text-steel-300 hover:border-copper"
            }`}
          >
            ALL
          </button>
          {STATUS.map((s) => (
            <button
              key={s.v}
              data-testid={`admin-filter-${s.v}`}
              onClick={() => setStatusFilter(s.v)}
              className={`px-3 py-2 font-mono-tech text-[10px] border ${
                statusFilter === s.v ? "border-copper bg-copper text-ink-900" : "border-ink-600 text-steel-300 hover:border-copper"
              }`}
            >
              {s.label.toUpperCase()} ({stats.by_status?.[s.v] || 0})
            </button>
          ))}
          <div className="ml-auto flex items-center border border-ink-600 bg-ink-800 min-w-[280px]">
            <Search size={14} className="ml-3 text-steel-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, vehicle, VRN…"
              data-testid="admin-search"
              className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-steel-500"
            />
          </div>
        </div>
      </section>

      {/* Table */}
      <section>
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-8">
          {loading ? (
            <div className="border border-ink-600 p-16 text-center font-mono-tech text-xs text-steel-400">
              LOADING QUOTES…
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-ink-600 p-16 text-center">
              <div className="font-display text-2xl mb-2">No quotes.</div>
              <p className="text-steel-400 text-sm">Fresh leads will appear here as they come in.</p>
            </div>
          ) : (
            <div className="border border-ink-600 overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-ink-800 border-b border-ink-600">
                  <tr>
                    {["Received", "Customer", "Vehicle / Part", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono-tech text-[10px] text-steel-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((qq) => (
                    <QuoteRow
                      key={qq.id}
                      q={qq}
                      expanded={expanded === qq.id}
                      onToggle={(id) => setExpanded(expanded === id ? null : id)}
                      onUpdate={update}
                      onDelete={del}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <main className="min-h-[80vh] grid place-items-center font-mono-tech text-xs text-steel-400">
        LOADING…
      </main>
    );
  }
  if (!user) return <LoginScreen />;
  return <Dashboard />;
}
